import React from "react";
import { ExternalLink, Facebook, Twitter, Instagram, Youtube, Linkedin, Link as LinkIcon } from "lucide-react";
import { motion } from "framer-motion";

interface CustomLink {
  label: string;
  url: string;
  icon?: string;
}

interface ProfileSocialLinksExpandedProps {
  profile: {
    custom_links?: CustomLink[];
  };
}

const SOCIAL_ICONS: { [key: string]: React.ComponentType<any> } = {
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  youtube: Youtube,
  linkedin: Linkedin,
};

const ProfileSocialLinksExpanded: React.FC<ProfileSocialLinksExpandedProps> = ({ profile }) => {
  if (!profile?.custom_links || !Array.isArray(profile.custom_links)) {
    return null;
  }

  // Filter out empty or invalid links
  const customLinks = profile.custom_links.filter((link: any) => {
    return link && 
           typeof link === 'object' && 
           link.label && 
           typeof link.label === 'string' && 
           link.label.trim() !== '' &&
           link.url && 
           typeof link.url === 'string' && 
           link.url.trim() !== '';
  });

  if (customLinks.length === 0) {
    return null;
  }

  const getIconComponent = (iconName: string) => {
    if (!iconName) return LinkIcon;
    const IconComponent = SOCIAL_ICONS[iconName.toLowerCase()];
    return IconComponent || LinkIcon;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto"
    >
      {customLinks.map((link, index) => {
        const IconComponent = getIconComponent(link.icon);
        
        return (
          <motion.a
            key={`${link.label}-${index}`}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.1, duration: 0.2 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group flex items-center justify-between p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 transition-all duration-200"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors duration-200">
                <IconComponent className="h-5 w-5 text-white" />
              </div>
              <span className="text-white font-medium">{link.label}</span>
            </div>
            <ExternalLink className="h-4 w-4 text-white/60 group-hover:text-white transition-colors duration-200" />
          </motion.a>
        );
      })}
    </motion.div>
  );
};

export default ProfileSocialLinksExpanded;
